import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { shopperApi } from '../lib/shopperApi';
import { ApiError } from '../lib/http';
import { WishlistEntry } from '../types/api';

interface WishlistState {
  /** Wished item ids, newest first; guests keep them only on this device. */
  ids: string[];
  /** Cards for the ids, when the account has supplied them. */
  entries: WishlistEntry[];
  synced: boolean;
  error: string | null;
  /** Add or remove the heart; saved to the account when signed in. */
  toggle: (itemId: string) => Promise<void>;
  hydrate: (entries: readonly WishlistEntry[]) => void;
  reset: () => void;
}

/** The backend accepts at most this many ids in one merge. */
export const MAX_WISHLIST_MERGE = 200;

export const useWishlistStore = create<WishlistState>()(
  persist(
    (set, get) => ({
      ids: [],
      entries: [],
      synced: false,
      error: null,

      toggle: async (itemId) => {
        const before = get().ids;
        const adding = !before.includes(itemId);
        set({
          ids: adding ? [itemId, ...before] : before.filter((id) => id !== itemId),
          entries: adding ? get().entries : get().entries.filter((e) => e.item_id !== itemId),
          error: null,
        });
        if (!get().synced) return;
        try {
          await (adding ? shopperApi.addWish(itemId) : shopperApi.removeWish(itemId));
        } catch (error) {
          set({
            ids: before,
            error: error instanceof ApiError ? error.message : 'Could not update your wishlist.',
          });
        }
      },

      hydrate: (entries) =>
        set({ ids: entries.map((e) => e.item_id), entries: [...entries], synced: true, error: null }),

      reset: () => set({ ids: [], entries: [], synced: false, error: null }),
    }),
    {
      name: 'rt-wishlist',
      version: 1,
      partialize: (state) => ({ ids: state.ids, synced: state.synced }),
    },
  ),
);

export const selectIsWished =
  (itemId: string) =>
  (state: WishlistState): boolean =>
    state.ids.includes(itemId);
